import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { EngagementScore, PillarScore } from '../../../services/gbtaService';
import { Button } from '../Button';

interface GBTAEngagementScoreProps {
  engagementScore: EngagementScore;
  onClose?: () => void;
}

// Styled components
const EngagementContainer = styled.div`
  margin-top: 1rem;
`;

const EngagementTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--color-gray-900);
`;

const EngagementDescription = styled.p`
  font-size: 0.875rem;
  color: var(--color-gray-600);
  margin-bottom: 1.5rem;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ChartLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-gray-700);
  margin-bottom: 0.25rem;
`;

const ChartSubtext = styled.div`
  font-size: 0.75rem;
  color: var(--color-gray-600);
  margin-top: 0.25rem;
`;

const ChartWrapper = styled.div`
  width: 140px;
  height: 140px;
  margin: 0.75rem 0;
  position: relative;
`;

const PillarNote = styled.p`
  font-size: 0.875rem;
  color: var(--color-gray-600);
  font-style: italic;
  margin-top: 0.5rem;
`;

// Color mapping for pillars
const PILLAR_COLORS = {
  alo: 'var(--color-gray-700)', // Gray-700
  e: 'var(--color-planet-green)', // Green-500 (Planet)
  s: 'var(--color-people-blue)', // Blue-500 (People)
  g: 'var(--color-policy-orange)', // Amber-500 (Policy)
};

const PILLAR_NAMES = {
  alo: 'Total',
  e: 'Planet',
  s: 'People',
  g: 'Policy',
};

const GBTAEngagementScore: React.FC<GBTAEngagementScoreProps> = ({
  engagementScore,
  onClose,
}) => {
  const renderPieChart = (key: keyof EngagementScore, score: PillarScore) => {
    const answered = score.answered;
    const percentage = score.engagement_percentage;

    // Use percentage directly so the pie fills according to engagement %,
    // not raw answered/skipped counts (which can be unequal in magnitude)
    const data = [
      { name: 'Answered', value: percentage, percentage },
      { name: 'Skipped', value: 100 - percentage, percentage },
    ];

    const COLORS = [PILLAR_COLORS[key], '#E5E7EB']; // Active color and gray for skipped

    return (
      <ChartCard key={key}>
        <ChartLabel>
          {PILLAR_NAMES[key]}: {score.total_questions}
        </ChartLabel>
        <ChartSubtext>Answered: {answered}</ChartSubtext>
        <ChartSubtext>Score: {percentage}%</ChartSubtext>

        <ChartWrapper>
          <ResponsiveContainer width={140} height={140}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={60}
                paddingAngle={2}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </ChartCard>
    );
  };

  return (
    <EngagementContainer>
      <EngagementTitle>Engagement</EngagementTitle>
      <EngagementDescription>
        Engagement reflects the percentage of questions answered.
      </EngagementDescription>

      <ChartsGrid className="bg-white p-4 rounded-lg shadow">
        {renderPieChart('alo', engagementScore.alo)}
        {renderPieChart('e', engagementScore.e)}
        {renderPieChart('s', engagementScore.s)}
        {renderPieChart('g', engagementScore.g)}
      </ChartsGrid>

      {/* <PillarNote>
        Select a pillar to see skipped questions.
      </PillarNote> */}
      <Button variant="outline" onClick={onClose}>
        Go to questionnaire
      </Button>
    </EngagementContainer>
  );
};

export default GBTAEngagementScore;
